import React, { useState } from 'react';
import type { FC } from 'react';
import { match } from 'variant';
import { Box, Text, Alert, Spinner, useAsyncCallback } from '@wix/cli-ui-kit';
import { Trans } from '@wix/cli-i18n';
import { useARMClient } from '@wix/arm-client';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { parse } from 'semver';
import { useProjectModel } from '../../model.js';
import { CreatePreview } from './Steps/CreatePreview.js';
import { Warnings } from './Steps/Warnings.js';
import { ReleaseComment } from './Steps/ReleaseComment.js';
import { ChooseVersion } from './Steps/ChooseVersion.js';
import { ConfirmMinorVersion } from './Steps/ConfirmMinorVersion.js';
import { NextSteps } from './Steps/NextSteps.js';
import { ReleaseVersionType } from './types.js';

interface ReleaseProps {
  baseUrl?: string;
  siteIdOverride?: string;
  preEnteredComment?: string;
  preSelectedVersionType?: ReleaseVersionType;
  onReleaseDraftCreated?: (version: string) => void;
}

export const Release: FC<ReleaseProps> = ({
  baseUrl,
  siteIdOverride,
  preEnteredComment,
  preSelectedVersionType,
  onReleaseDraftCreated,
}) => {
  const {
    model: {
      config: { appId },
    },
  } = useProjectModel();

  const { createDraftVersion, releaseDraftVersion } = useARMClient();

  const [tag, setTag] = useState<string>();
  const [comment, setComment] = useState<string | undefined>();
  const [versionType, setVersionType] = useState<ReleaseVersionType>();
  const [userShouldSelectVersion, setUserShouldSelectVersion] = useState(false);

  const createDraftAction = useAsyncCallback(async (_, tag: string) => {
    const { nextMajor, nextMinor } = await createDraftVersion(appId, tag);
    const appWasNeverReleased = nextMajor === '1.0.0' && !nextMinor;

    const parsedNextMajor = parse(nextMajor);
    if (parsedNextMajor == null) {
      throw new CliError({
        code: CliErrorCode.FailedToParseNextVersion({ version: nextMajor }),
        cause: undefined,
      });
    }

    return {
      nextMinor,
      nextMajor,
      appWasNeverReleased,
      latestMajor: parsedNextMajor.major - 1,
    };
  });

  const releaseDraftAction = useAsyncCallback(
    async (
      _,
      versionType: ReleaseVersionType,
      comment: string,
      latestMajor: number
    ) => {
      const result = await releaseDraftVersion(appId, {
        createNewMajor: versionType === ReleaseVersionType.MAJOR,
        notes: comment,
        latestMajor,
      });

      onReleaseDraftCreated?.(result.releasedVersion);

      return result;
    }
  );

  function onPreviewCreated({ tag }: { tag: string }) {
    setTag(tag);

    if (isString(preEnteredComment)) {
      void onReleaseCommentEntered({ tag, comment: preEnteredComment });
    }
  }

  async function onReleaseCommentEntered({
    tag,
    comment,
  }: {
    tag: string;
    comment: string;
  }) {
    setComment(comment);

    const result = await createDraftAction.execute(tag).catch(() => null);
    if (!result) {
      return;
    }

    const { appWasNeverReleased, nextMinor, latestMajor } = result;

    const chosenVersion = appWasNeverReleased
      ? ReleaseVersionType.MAJOR
      : preSelectedVersionType;

    if (
      preSelectedVersionType === ReleaseVersionType.MINOR &&
      (appWasNeverReleased || !nextMinor)
    ) {
      throw new CliError({
        cause: null,
        code: CliErrorCode.CannotReleaseMinorInNoninteractive(),
      });
    }

    if (chosenVersion) {
      void releaseDraftAction.execute(chosenVersion, comment, latestMajor);
    } else {
      setUserShouldSelectVersion(true);
    }
  }

  return (
    <>
      <CreatePreview
        baseUrl={baseUrl}
        siteIdOverride={siteIdOverride}
        onCreated={(tag) => onPreviewCreated({ tag })}
      />
      {tag && <Warnings />}
      {tag && !isString(preEnteredComment) && (
        <ReleaseComment
          onEntered={(comment) => onReleaseCommentEntered({ tag, comment })}
        />
      )}
      {isString(comment) &&
        match(createDraftAction.status, {
          NotRequested: () => null,
          Error: () => null,
          Loading: () => (
            <Spinner
              text={<Trans i18nKey="release_command.creating_draft" />}
            />
          ),
          Success: ({ result: { nextMajor, nextMinor, latestMajor } }) => (
            <>
              {userShouldSelectVersion && (
                <ChooseVersion
                  nextMinor={nextMinor}
                  nextMajor={nextMajor}
                  onChosen={(selectedVersionType) => {
                    setVersionType(selectedVersionType);
                    if (selectedVersionType === ReleaseVersionType.MAJOR) {
                      void releaseDraftAction.execute(
                        selectedVersionType,
                        comment,
                        latestMajor
                      );
                    }
                  }}
                />
              )}
              {versionType === ReleaseVersionType.MINOR && (
                <ConfirmMinorVersion
                  onConfirmed={() =>
                    releaseDraftAction.execute(
                      versionType,
                      comment,
                      latestMajor
                    )
                  }
                />
              )}
            </>
          ),
        })}
      {match(releaseDraftAction.status, {
        NotRequested: () => null,
        Error: () => null,
        Loading: () => (
          <Spinner text={<Trans i18nKey="release_command.releasing" />} />
        ),
        Success: ({ result: { releasedVersion } }) => (
          <Box flexDirection="column" gap={1}>
            <Alert type="success">
              <Text bold>
                <Trans
                  i18nKey="release_command.released"
                  values={{ version: releasedVersion }}
                />
              </Text>
            </Alert>
            <NextSteps />
          </Box>
        ),
      })}
    </>
  );
};

function isString(value: unknown): value is string {
  return typeof value === 'string';
}
